import { useMemo, useState } from "react";
import type { ApiKey, Qubic, Subscription } from "api";
import cn from "classnames";
import { Modal } from "components/ui/Modal";
import type { useApiKeys } from "hooks/useApiKeys";
import { keyProducts, keyRow } from "./apikeys.model";
import { CreateKeyRow } from "./CreateKeyRow";
import { RevealedKey } from "./RevealedKey";

type ApiKeysPanelProps = {
  keys: ReturnType<typeof useApiKeys>;
  qubics: Qubic[];
  subscriptions: Subscription[];
};

export const ApiKeysPanel = ({ keys, qubics, subscriptions }: ApiKeysPanelProps) => {
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);

  // With nothing on the list the create row is the whole point of the card, so
  // it stands open instead of hiding behind a button.
  const noKeys = keys.status === "ready" && keys.keys.length === 0;
  const rowOpen = creatingOpen || noKeys;

  const products = useMemo(() => keyProducts(qubics), [qubics]);

  return (
    <div className="pcx">
      <div className="pcx__top">
        <div>
          <div className="pcx__h">API keys</div>
          <p className="pcx__sub">API keys are shown only once when created.</p>
          <p className="pcx__sub">Use them to authenticate server-to-server requests to the Qubictron API.</p>
        </div>
        {!noKeys && keys.status === "ready" && (
          <button type="button" className="pcbtn pcbtn--main" onClick={() => setCreatingOpen((current) => !current)}>
            {rowOpen ? "Close" : "Create key"}
          </button>
        )}
      </div>

      {rowOpen && (
        <CreateKeyRow
          products={products}
          creating={keys.creating}
          onSubmit={(name, tool) => {
            void keys.create({ name, service_tool: tool }).then((created) => {
              if (created) setCreatingOpen(false);
            });
          }}
        />
      )}

      {keys.createError && <p className="pc-note pc-note--bad">{keys.createError}</p>}

      {keys.status === "loading" && <span className="pc-spin" aria-label="Loading" />}

      {keys.status === "failed" && (
        <p className="pc-note pc-note--bad">Could not load your API keys. Please refresh the page.</p>
      )}

      {keys.status === "unverified" && <p className="pc-note">Verify your email address to manage API keys.</p>}

      {keys.revokeError && <p className="pc-note pc-note--bad">{keys.revokeError}</p>}

      {keys.status === "ready" && keys.keys.length > 0 && (
        <div className="pc-keys">
          {keys.keys.map((key) => {
            const row = keyRow(key, qubics, subscriptions);
            // The new key carries its one-time reveal in place of the "hidden" line.
            const revealed = keys.createdKey?.id === key.id ? keys.createdKey : null;

            return (
              <div className={cn("pc-key", { "pc-key--new": revealed })} key={key.id}>
                <div>
                  <div className="pc-key__name">
                    {row.cube && <img className="pccube" src={row.cube} alt="" width={24} height={24} />}
                    {key.name}
                    {row.tag && <span className={cn("pc-tag", row.tag.modifier)}>{row.tag.label}</span>}
                  </div>
                  {!revealed && <div className="pc-key__hidden">Key hidden — it was shown only once, at creation</div>}
                  <div className="pc-key__note">{row.note}</div>
                  {revealed && <RevealedKey createdKey={revealed} onDismiss={keys.dismissCreatedKey} />}
                </div>

                <div className="pc-key__acts">
                  {row.quoteUrl && (
                    <a className="pcbtn" href={row.quoteUrl} target="_blank" rel="noopener noreferrer">
                      Request a Quote
                    </a>
                  )}
                  {/* Every key goes through the modal: without `last_used_at` we
                      cannot tell a live key from an idle one. */}
                  <button
                    type="button"
                    className="pcbtn pcbtn--bad"
                    disabled={keys.revokingId === key.id}
                    onClick={() => setKeyToRevoke(key)}
                  >
                    {keys.revokingId === key.id ? "Revoking…" : "Revoke"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {keyToRevoke && (
        <Modal
          title="Revoke this key?"
          onClose={() => setKeyToRevoke(null)}
          actions={
            <>
              <button
                type="button"
                className="pcbtn pcbtn--danger"
                onClick={() => {
                  void keys.revoke(keyToRevoke.id);
                  setKeyToRevoke(null);
                }}
              >
                Revoke key
              </button>
              <button type="button" className="pcbtn" onClick={() => setKeyToRevoke(null)}>
                Cancel
              </button>
            </>
          }
        >
          <p className="pcmodal__t">
            Any service authenticating with <b>{keyToRevoke.name}</b> will start receiving 401 errors immediately. This
            cannot be undone.
          </p>
        </Modal>
      )}
    </div>
  );
};
